"use client";

import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class VeriffErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a Veriff-related error
    const isVeriffError = 
      error.message?.includes('Veriff') ||
      error.message?.includes('veriff') ||
      error.stack?.includes('veriff.js') ||
      error.stack?.includes('veriffSDK') ||
      error.message?.includes('focus') ||
      error.message?.includes('FocusScope');

    if (isVeriffError) {
      console.warn('[VeriffErrorBoundary] Caught Veriff-related error:', error.message);
      return { hasError: true, error };
    }

    // For non-Veriff errors, let them bubble up
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.warn('[VeriffErrorBoundary] Error details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    this.setState({
      error,
      errorInfo
    });

    // Track the error but don't spam PostHog with Veriff focus loops
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('veriff_error_boundary_triggered', {
        error_message: error.message,
        error_stack: error.stack?.substring(0, 500), // Limit stack trace size
        is_focus_loop: error.message?.includes('focus') || error.stack?.includes('FocusScope')
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI for Veriff errors
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.725-1.36 3.49 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-orange-800">
                Verification Service Temporarily Unavailable
              </h3>
              <p className="mt-1 text-sm text-orange-700">
                The identity verification service is experiencing issues. Please try again in a moment.
              </p>
              <div className="mt-3">
                <button
                  onClick={this.handleRetry}
                  className="text-sm font-medium text-orange-800 hover:text-orange-900 underline"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useVeriffErrorHandler() {
  const handleVeriffError = (error: Error) => {
    const isVeriffError = 
      error.message?.includes('Veriff') ||
      error.message?.includes('veriff') ||
      error.stack?.includes('veriff.js') ||
      error.stack?.includes('veriffSDK') ||
      error.message?.includes('focus') ||
      error.message?.includes('FocusScope');

    if (isVeriffError) {
      console.warn('[VeriffErrorHandler] Caught Veriff error:', error.message);
      // Prevent the error from propagating
      return true;
    }
    return false;
  };

  return { handleVeriffError };
}
